import React from 'react';
import {
  Cell,
  Pie,
  PieChart as RechartsPieChart,
  ResponsiveContainer,
} from 'recharts';
import { createPortal } from 'react-dom';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@roger/ui-components';
import { HelpCircle } from 'lucide-react';
import {
  hasUsableData,
  type DashboardWidgetData,
  type DashboardWidgetState,
} from '../../../hooks/dashboard/useDashboardQuery';
import type {
  WidgetItemLimits,
  WidgetViewPreferenceKey as PreferenceKey,
  WidgetView,
} from '../../../hooks/dashboard/dashboardConfig';
import { CHART_ITEM_LIMIT_MAX } from './boardConfig';
import './dashboardWidgets.css';
import {
  NEUTRAL_SERIES_COLOR,
  SERIES_COLOR_COUNT,
  chartColor,
} from './chartColors';

export type PieListItem = {
  id: string;
  label: string;
  count: number;
  percentage?: number;
  rank?: number;
};

function getWidgetData(
  state: DashboardWidgetState
): DashboardWidgetData | null {
  return hasUsableData(state) ? state.data : null;
}

export const CENSUS_TOOLTIP_EXPLANATION =
  'Admitted patients with an actual Start of Care (SOC) on or before today who have not been discharged. Open admitted census includes active home patients and patients on hospital hold.';

export function ActivePatientCensusWidget({
  state,
}: {
  state: DashboardWidgetState;
}) {
  const data = getWidgetData(state);
  if (!data || data.kind !== 'active_patient_census') return null;

  const hasBreakdown =
    data.active_home_count !== undefined &&
    data.hospital_hold_count !== undefined;

  return (
    <div className='flex flex-1 flex-col justify-center text-center'>
      <div className='relative flex items-center justify-center'>
        <Stat count={data.count} />
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type='button'
                aria-label='Census calculation details'
                className='absolute right-2 top-0 inline-flex rounded p-1 text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
              >
                <HelpCircle className='h-4 w-4' aria-hidden='true' />
              </button>
            </TooltipTrigger>
            <TooltipContent className='max-w-xs text-xs leading-normal'>
              {CENSUS_TOOLTIP_EXPLANATION}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
      {hasBreakdown ? (
        <div className='mt-2 text-xs text-muted-foreground'>
          <span>{data.active_home_count} Active</span>
          <span className='mx-1.5'>·</span>
          <span>{data.hospital_hold_count} Hospital Hold</span>
        </div>
      ) : null}
    </div>
  );
}

// A big count over an optional one-line label; the census and OASIS cards
// share it.
function Stat({
  count,
  label,
  muted = false,
}: {
  count: number;
  label?: string;
  muted?: boolean;
}) {
  return (
    <div className='min-w-0'>
      <div
        className={`dash-big-number text-4xl font-semibold tabular-nums leading-none ${
          muted ? 'text-muted-foreground' : ''
        }`}
      >
        {count}
      </div>
      {label ? (
        <div className='mt-1.5 truncate text-sm text-muted-foreground'>
          {label}
        </div>
      ) : null}
    </div>
  );
}

// The card title already says OASIS, so each count needs only its state.
const OASIS_LABELS: Record<string, string> = {
  assigned_not_completed: 'Not completed',
  completed_not_exported: 'Not exported',
};

export function TaskCategoryWidget({
  state,
  view,
  query,
  itemLimits,
}: {
  state: DashboardWidgetState;
  view: WidgetView;
  query: Extract<PreferenceKey, 'case_manager_queue' | 'past_due_tasks'>;
  itemLimits: WidgetItemLimits;
}) {
  const data = getWidgetData(state);
  if (!data || data.kind !== query) return null;
  return (
    <PieListWidget
      denominator={data.total}
      unit={data.unit}
      items={data.categories}
      view={view}
      chartLabel={
        query === 'case_manager_queue' ? 'Case manager queue' : 'Past due tasks'
      }
      itemLimit={itemLimits[view]}
    />
  );
}

export function OutstandingOasisWidget({
  state,
}: {
  state: DashboardWidgetState;
}) {
  const data = getWidgetData(state);
  if (!data || data.kind !== 'outstanding_oasis') return null;
  return (
    <div className='flex flex-1 flex-col justify-center text-center'>
      <div className='dash-metric-grid grid grid-cols-2 gap-x-3 gap-y-2'>
        {data.metrics.map(item => (
          <Stat
            key={item.id}
            count={item.count}
            label={OASIS_LABELS[item.id] ?? item.label}
            muted={item.count === 0}
          />
        ))}
      </div>
    </div>
  );
}

export function CensusSeriesWidget({
  query,
  state,
  view,
  itemLimits,
}: {
  query: Extract<
    PreferenceKey,
    'census_by_primary_insurer' | 'census_by_primary_diagnosis'
  >;
  state: DashboardWidgetState;
  view: WidgetView;
  itemLimits: WidgetItemLimits;
}) {
  const data = getWidgetData(state);
  if (!data || data.kind !== query) return null;
  return (
    <PieListWidget
      denominator={data.denominator}
      unit={data.unit}
      items={data.items}
      view={view}
      chartLabel={
        query === 'census_by_primary_insurer'
          ? 'Census by primary insurer'
          : 'Census by primary diagnosis'
      }
      itemLimit={itemLimits[view]}
      ordered
    />
  );
}

// Whole numbers from 1%, one significant figure below it: 64%, 1%, 0.8%,
// 0.03%. The count carries the detail.
function formatPercent(value: number): string {
  if (value === 0) return '0%';
  if (value >= 1) return `${Math.round(value)}%`;
  return `${Number(value.toPrecision(1))}%`;
}

function RowCount({
  count,
  spaced = false,
}: {
  count: number | string;
  spaced?: boolean;
}) {
  return (
    <span
      className={`relative shrink-0 text-sm font-medium tabular-nums ${
        spaced ? 'pl-4' : ''
      }`}
    >
      {count}
    </span>
  );
}

// A bar behind the whole row, label and count; a full one spans the row.
function RowBar({ width }: { width: number }) {
  return (
    <span
      className='absolute inset-y-0 left-0 rounded-sm bg-primary/[0.12]'
      style={{ width: `${width}%` }}
      aria-hidden='true'
    />
  );
}

function formatDisplayPercentage(item: PieListItem, denominator: number) {
  if (item.percentage !== undefined) return formatPercent(item.percentage);
  if (denominator === 0) return '0%';
  return formatPercent((item.count / denominator) * 100);
}

const SPECIAL_SERIES_ITEM_IDS = new Set(['other', 'none', 'unknown']);

// Each item's colour, decided once so the pie and its legend agree: the
// series colours in order, and the neutral grey only for an eighth slice.
function seriesColors(items: PieListItem[]): Map<string, string> {
  return new Map(
    items.map((item, index) => [
      item.id,
      index === SERIES_COLOR_COUNT ? NEUTRAL_SERIES_COLOR : chartColor(index),
    ])
  );
}

function mergedPercentage(
  items: PieListItem[],
  count: number,
  denominator: number
): number | undefined {
  if (items.every(item => item.percentage !== undefined)) {
    return Number(
      items.reduce((sum, item) => sum + (item.percentage ?? 0), 0).toFixed(2)
    );
  }
  if (denominator === 0) return undefined;
  return Number(((count / denominator) * 100).toFixed(2));
}

function reduceDisplayItems(
  items: PieListItem[],
  denominator: number,
  itemLimit: number
): PieListItem[] {
  const regularItems = items.filter(
    item => !SPECIAL_SERIES_ITEM_IDS.has(item.id)
  );
  const specialItems = items.filter(item =>
    SPECIAL_SERIES_ITEM_IDS.has(item.id)
  );

  // The limit counts every row, Other included, so it is at least 2.
  const limit = Math.max(2, itemLimit);
  if (items.length <= limit) {
    return [...regularItems, ...specialItems];
  }

  const keep = Math.max(
    0,
    limit - 1 - specialItems.filter(item => item.id !== 'other').length
  );
  const visibleRegularItems = regularItems.slice(0, keep);
  const hiddenRegularItems = regularItems.slice(keep);
  const hiddenCount = hiddenRegularItems.reduce(
    (sum, item) => sum + item.count,
    0
  );

  if (hiddenCount === 0) return [...visibleRegularItems, ...specialItems];

  const existingOther = specialItems.find(item => item.id === 'other');
  const nonOtherSpecialItems = specialItems.filter(item => item.id !== 'other');
  const otherItems = existingOther
    ? [existingOther, ...hiddenRegularItems]
    : hiddenRegularItems;
  const otherCount = hiddenCount + (existingOther ? existingOther.count : 0);
  const mergedOther: PieListItem = {
    id: 'other',
    label: existingOther?.label ?? 'Other',
    count: otherCount,
    percentage: mergedPercentage(otherItems, otherCount, denominator),
    rank: existingOther?.rank ?? visibleRegularItems.length + 1,
  };

  return [...visibleRegularItems, mergedOther, ...nonOtherSpecialItems];
}

// Report widgets pass formatCount to show money, may leave unit empty, and
// turn off the share for an average, whose sum means nothing.
export function PieListWidget({
  denominator,
  unit,
  items,
  view,
  chartLabel,
  itemLimit,
  ordered = false,
  formatCount = String,
  showPercent = true,
}: {
  denominator: number;
  unit: string;
  items: PieListItem[];
  view: WidgetView;
  chartLabel: string;
  itemLimit: number;
  ordered?: boolean;
  formatCount?: (count: number) => string;
  showPercent?: boolean;
}) {
  const ListTag = ordered ? 'ol' : 'ul';
  const displayItems = reduceDisplayItems(
    items,
    denominator,
    view === 'chart' ? Math.min(itemLimit, CHART_ITEM_LIMIT_MAX) : itemLimit
  );
  const colors = seriesColors(displayItems);
  const maxCount = Math.max(1, ...displayItems.map(item => item.count));
  const hover = useHoverCard();
  const detail = (item: PieListItem) =>
    [
      [formatCount(item.count), unit].filter(Boolean).join(' '),
      showPercent ? formatDisplayPercentage(item, denominator) : '',
    ]
      .filter(Boolean)
      .join(' · ');

  // An empty card says so, rather than showing a blank body.
  if (denominator === 0 && items.length === 0) {
    return <p className='text-sm text-muted-foreground'>No {unit}</p>;
  }

  return (
    <div className='flex flex-1 flex-col gap-2'>
      <div
        className={
          view === 'chart'
            ? 'dash-pie-layout dash-pie-layout--chart flex-1'
            : 'dash-pie-layout'
        }
      >
        {view === 'chart' && denominator > 0 ? (
          <PieChart
            items={displayItems}
            colors={colors}
            label={chartLabel}
            onSliceHover={(item, x, y) =>
              hover.show(item.label, detail(item), x, y)
            }
            onSliceLeave={hover.clear}
          />
        ) : null}
        <ListTag
          className={
            view === 'chart'
              ? 'dash-pie-legend min-w-0 space-y-0'
              : 'min-w-0 flex-1 space-y-1'
          }
        >
          {displayItems.map(item => (
            <li
              key={item.id}
              className={`relative flex items-center gap-3 ${
                view === 'list' ? 'pr-2' : ''
              }`}
              {...hover.bind(item.label, detail(item))}
            >
              {view === 'list' ? (
                <RowBar width={(item.count / maxCount) * 100} />
              ) : null}
              {ordered && item.rank && view === 'list' ? (
                <span className='relative w-5 text-xs tabular-nums text-muted-foreground'>
                  {item.rank}.
                </span>
              ) : null}
              {view === 'chart' ? (
                <span
                  className='relative h-2.5 w-2.5 shrink-0 rounded-full'
                  style={{ backgroundColor: colors.get(item.id) }}
                  aria-hidden='true'
                />
              ) : null}
              <span
                className={`relative block min-w-0 flex-1 truncate px-2 text-sm ${
                  view === 'chart' ? 'py-0.5' : 'py-1'
                }`}
              >
                {item.label}
              </span>
              {/* A pie legend hugs its names, so its counts need more room. */}
              <RowCount
                count={formatCount(item.count)}
                spaced={view === 'chart'}
              />
            </li>
          ))}
        </ListTag>
      </div>
      {hover.card}
    </div>
  );
}

type Hover = { title: string; text: string; x: number; y: number };

// Fixed to the pointer at the page level, so it never stretches a card's
// scroll area; it opens away from the nearer window edge.
function HoverCard({ title, text, x, y }: Hover) {
  const flipX = x > window.innerWidth / 2;
  const flipY = y > window.innerHeight / 2;
  return createPortal(
    <div
      className='pointer-events-none fixed z-50 max-w-56 rounded-lg border bg-popover px-3 py-2 text-sm text-popover-foreground shadow-md'
      style={{
        left: x,
        top: y,
        transform: `translate(${flipX ? 'calc(-100% - 14px)' : '14px'}, ${
          flipY ? 'calc(-100% - 14px)' : '14px'
        })`,
      }}
    >
      <div className='font-semibold'>{title}</div>
      <div className='mt-1 text-muted-foreground'>{text}</div>
    </div>,
    document.body
  );
}

// Percentages live in this card, shown over a row or a slice.
function useHoverCard() {
  const [hover, setHover] = React.useState<Hover | null>(null);
  const show = (title: string, text: string, x: number, y: number) =>
    setHover({ title, text, x, y });
  const clear = () => setHover(null);
  const bind = (title: string, text: string) => ({
    onMouseMove: (event: React.MouseEvent) =>
      show(title, text, event.clientX, event.clientY),
    onMouseLeave: clear,
  });
  return {
    show,
    clear,
    bind,
    card: hover ? <HoverCard {...hover} /> : null,
  };
}

function AgeBarChart({
  unit,
  buckets,
}: {
  unit: string;
  buckets: Array<{
    id: string;
    label: string;
    count: number;
    percentage: number;
  }>;
}) {
  const maxCount = Math.max(...buckets.map(bucket => bucket.count), 0);
  const hover = useHoverCard();
  // One line per bucket with the bar behind its label, like the list cards.
  return (
    <ul className='space-y-1'>
      {hover.card}
      {buckets.map(bucket => {
        const width = maxCount > 0 ? (bucket.count / maxCount) * 100 : 0;
        return (
          <li
            key={bucket.id}
            className='relative flex items-center gap-3 pr-2'
            {...hover.bind(
              bucket.label,
              `${bucket.count} ${unit} · ${formatPercent(bucket.percentage)}`
            )}
          >
            <RowBar width={width} />
            <span className='relative block min-w-0 flex-1 truncate px-2 py-0.5 text-sm'>
              {bucket.label}
            </span>
            <RowCount count={bucket.count} />
          </li>
        );
      })}
    </ul>
  );
}

function PieChart({
  items,
  colors,
  label,
  onSliceHover,
  onSliceLeave,
}: {
  items: PieListItem[];
  colors: Map<string, string>;
  label: string;
  onSliceHover: (item: PieListItem, x: number, y: number) => void;
  onSliceLeave: () => void;
}) {
  const segments = items.filter(item => item.count > 0);

  // A pie with thin card-coloured gaps, sized to the card (.dash-pie-chart).
  return (
    <div
      role='img'
      aria-label={`${label} pie chart`}
      className='dash-pie-chart'
      // Recharts' own leave event can miss, so the box clears the card too:
      // on leaving it, or over its background rather than a slice.
      onMouseLeave={onSliceLeave}
      onMouseMove={event => {
        if (!(event.target as Element).closest('.recharts-pie-sector')) {
          onSliceLeave();
        }
      }}
    >
      <ResponsiveContainer width='100%' height='100%'>
        <RechartsPieChart>
          <Pie
            data={segments}
            dataKey='count'
            nameKey='label'
            cx='50%'
            cy='50%'
            outerRadius='98%'
            startAngle={90}
            endAngle={-270}
            // One whole slice has no gap to draw, only a seam to the centre.
            stroke={segments.length > 1 ? 'hsl(var(--card))' : 'none'}
            strokeWidth={1.5}
            // Square joins spike past the centre where the slices meet.
            strokeLinejoin='round'
            isAnimationActive={false}
            onMouseMove={(sector: { payload?: PieListItem }, _index, event) =>
              sector.payload &&
              onSliceHover(sector.payload, event.clientX, event.clientY)
            }
            onMouseLeave={onSliceLeave}
          >
            {segments.map(item => (
              <Cell key={item.id} fill={colors.get(item.id)} />
            ))}
          </Pie>
        </RechartsPieChart>
      </ResponsiveContainer>
    </div>
  );
}

export function OrdersPendingSignatureWidget({
  state,
}: {
  state: DashboardWidgetState;
}) {
  const data = getWidgetData(state);
  if (!data || data.kind !== 'orders_pending_signature') return null;
  return <AgeBarChart unit={data.unit} buckets={data.buckets} />;
}
